import { trackOnce } from "../utils/tracking";
import { SURVEY_QUESTION, SURVEY_STARTED } from "../constants/posthogEvents";
import { PostHog } from "posthog-js";
import { getGoalMonths } from "./weightUtils";
import { SurveySession } from "../typings";
import { Membership } from "../services/stripe";
import { AppEntrypoint, MSO } from "../typings/enums";
import { getFeatureFlag } from "../services/featureFlags";
import { POSTHOG_FEATURE_FLAGS } from "../services/posthogFeatureFlagDefinitions";

export const trackSurveyQuestion = (posthog: PostHog, index: number) => {
  let surveyEvent;
  switch (true) {
    case index === 0:
      surveyEvent = SURVEY_STARTED;
      break;
    default:
      surveyEvent = `${SURVEY_QUESTION}_${index}`;
  }

  void trackOnce(posthog, surveyEvent, {});
};

/**
 * Gets medicalProvider with fallback logic
 * Checks userProfile first, then falls back to surveySession
 * @param userProfile - User profile from users collection
 * @param surveySession - Survey session data
 * @returns The medicalProvider or undefined
 */
export const getMedicalProvider = (
  userProfile?: { medicalProvider?: MSO } | null,
  surveySession?: Partial<SurveySession> | null
): MSO | undefined => {
  return userProfile?.medicalProvider ?? surveySession?.user?.medicalProvider;
};

export enum ProductNames {
  SemaglutideMonthly = "semaglutide-monthly",
  Semaglutide2Months = "semaglutide-2-months",
  Semaglutide3Months = "semaglutide-3-months",
  Semaglutide4Months = "semaglutide-4-months",
  Semaglutide5Months = "semaglutide-5-months",
  Semaglutide6Months = "semaglutide-6-months",
  SemaglutideBundleMonthly = "semaglutide-bundle-monthly",
  SemaglutideBundle2Months = "semaglutide-bundle-2-months",
  SemaglutideBundle3Months = "semaglutide-bundle-3-months",
  SemaglutideBundle4Months = "semaglutide-bundle-4-months",
  SemaglutideBundle5Months = "semaglutide-bundle-5-months",
  SemaglutideBundle6Months = "semaglutide-bundle-6-months",
  SemaglutideBundle6MonthsAffirm = "semaglutide-bundle-6-months-affirm",
  OzempicBundleMonthly = "ozempic-bundle-monthly",
  OzempicBundle6Months = "ozempic-bundle-6-months",
  OzempicBundle3MonthsAffirm = "ozempic-bundle-3-months-affirm",
  OzempicBundle6MonthsAffirm = "ozempic-bundle-6-months-affirm",
  MounjaroBundleMonthly = "mounjaro-bundle-monthly",
  MounjaroBundle6Months = "mounjaro-bundle-6-months",
  MounjaroBundle3MonthsAffirm = "mounjaro-bundle-3-months-affirm",
  MounjaroBundle6MonthsAffirm = "mounjaro-bundle-6-months-affirm",
  InsuranceMounjaro = "amazon-insurance-mounjaro-monthly",
  NutritionProgram = "nutrition-program",
  NutritionMembershipDiscountedMonth1 = "nutrition-membership-discounted-month1",
  TirzepatideMicroPill = "tirzepatide-micro-pill",
  SemaglutideMicro = "semaglutide-micro",
  ZepboundBundleMonthly = "lillydirect-zepbound-bundle-monthly",
  ZepboundSplitMonthly = "lillydirect-zepbound-split-monthly",
  ZepboundBundle4MonthsAffirm = "lillydirect-zepbound-bundle-4-months-affirm",
  WegovyBundle4MonthsAffirm = "wegovy-bundle-4-months-affirm",
  WegovyBundle4MonthsAffirmDiscountIntroOffer = "wegovy-bundle-4-months-affirm-discount-intro-offer",
  WegovyBundleMonthly = "wegovy-bundle-monthly",
  WegovyBundleMonthlyUpsold = "wegovy-bundle-monthly-upsold",
  WegovyBundleMonthlyIntroOffer = "wegovy-bundle-monthly-intro-offer",
  WegovyBundleMonthlyJulyIntroOffer = "wegovy-bundle-monthly-july-intro-offer",
  WegovyPillBundleMonthly = "wegovy-bundle-monthly-oral",
  // Injectable semaglutide hybrid bundles
  InjectableSema3Months = "bundle-ol-isem-3month-med-included",
  InjectableSema4Months = "bundle-ol-isem-hybrid-4-months-v2-med-included",
  InjectableSema5Months = "bundle-ol-isem-hybrid-5-months-v2-med-included",
  InjectableSema6Months = "bundle-ol-isem-6month-med-included",
  InjectableSema12Months = "bundle-ol-isem-12month-med-included",
  InjectableSemaMonthly = "bundle-ol-isem-monthly-med-included",
  InjectableSema1MonthDiscount = "bundle-ol-isem-1month-med-included-100off",
  // Oral semaglutide hybrid bundles
  OralSema3Months = "bundle-ol-osem-3month-med-included",
  OralSema4Months = "bundle-ol-osem-hybrid-4-months-v2-med-included",
  OralSema5Months = "bundle-ol-osem-hybrid-5-months-v2-med-included",
  OralSema6Months = "bundle-ol-osem-6month-med-included",
  OralSema12Months = "bundle-ol-osem-12month-med-included",
  OralSemaMonthly = "bundle-ol-osem-monthly-med-included",
  // Injectable tirzepatide hybrid bundles
  InjectableTirz3Months = "bundle-ol-itirz-3month-med-included",
  InjectableTirz4Months = "bundle-ol-itirz-hybrid-4-months-v2-med-included",
  InjectableTirz5Months = "bundle-ol-itirz-hybrid-5-months-v2-med-included",
  InjectableTirz6Months = "bundle-ol-itirz-6month-med-included",
  InjectableTirz12Months = "bundle-ol-itirz-12month-med-included",
  InjectableTirzMonthly = "bundle-ol-itirz-monthly-med-included",
  InjectableTirz1MonthDiscount = "bundle-ol-itirz-1month-med-included-100off",
  // Oral tirzepatide hybrid bundles
  OralTirz3Months = "bundle-ol-otirz-3month-med-included",
  OralTirz4Months = "bundle-ol-otirz-hybrid-4-months-v2-med-included",
  OralTirz5Months = "bundle-ol-otirz-hybrid-5-months-v2-med-included",
  OralTirz6Months = "bundle-ol-otirz-6month-med-included",
  OralTirz12Months = "bundle-ol-otirz-12month-med-included",
  OralTirzMonthly = "bundle-ol-otirz-monthly-med-included",
  // Microdose
  MicroInjectableSema = "bundle-micro-ol-isem-monthly-199 ",
  MicroOralTirz = "bundle-micro-ol-otirz-monthly-249",
  // Open Loop Brand Meds
  OpenLoopOzempic = "bundle-ol-ozempic-monthly-med-included",
  OpenLoopZepbound = "bundle-ol-zepbound-monthly-med-included",
}

export const getProgramName = (
  surveySessionData: Partial<SurveySession>,
  selectedMembership: Membership
): string => {
  let programName = "";
  if (selectedMembership === Membership.NutritionProgram) {
    return ProductNames.NutritionProgram;
  } else if (selectedMembership === Membership.MonthlySubscription) {
    return ProductNames.SemaglutideBundleMonthly;
  } else if (selectedMembership === Membership.MultiMonthSubscription) {
    const goalWeight = surveySessionData.answers?.goal_weight ?? "0";
    const currentWeight =
      surveySessionData.answers?.height_weight?.weight ?? "0";

    const months = getGoalMonths(currentWeight, goalWeight);
    programName = getMultiMonthProgramName(months);
  }

  return programName;
};

export const getMultiMonthProgramName = (months: number): string => {
  switch (months) {
    case 2:
      return ProductNames.SemaglutideBundle2Months;
    case 3:
      return ProductNames.SemaglutideBundle3Months;
    case 4:
      return ProductNames.SemaglutideBundle4Months;
    case 5:
      return ProductNames.SemaglutideBundle5Months;
    case 6:
      return ProductNames.SemaglutideBundle6Months;
    default:
      return ProductNames.SemaglutideBundleMonthly;
  }
};

/**
 * Determines the app entrypoint based on medical provider, platform, source, and disqualification status
 * @param params - Object containing survey session data
 * @param params.medicalProvider - The medical provider from the survey session
 * @param params.source - The source from tracking (usually "app" or undefined)
 * @param params.isDisqualified - Whether the user is disqualified
 * @returns The appropriate AppEntrypoint value
 */
export const getAppEntrypoint = ({
  medicalProvider,
  source,
  isDisqualified,
}: {
  medicalProvider?: MSO;
  source?: string;
  isDisqualified?: boolean;
} = {}): AppEntrypoint => {
  // If user is disqualified, return mobile entrypoint
  if (isDisqualified) {
    return AppEntrypoint.fh_plus_mobile;
  }

  // Check if running in React Native WebView and source is "app"
  if (window?.ReactNativeWebView && source === "app") {
    return AppEntrypoint.fh_plus_mobile;
  }

  // Determine based on medical provider
  if (medicalProvider === MSO.steadymd) {
    return AppEntrypoint.fh_plus_web;
  }

  if (medicalProvider === MSO.openloop) {
    const useNewAppStoreLinks = getFeatureFlag(
      POSTHOG_FEATURE_FLAGS.USE_NEW_APP_STORE_LINKS
    );

    return useNewAppStoreLinks
      ? AppEntrypoint.fh_plus_mobile
      : AppEntrypoint.legacy_mobile;
  }

  // Default fallback to Glide. /account is being removed, so a new user who
  // hits this path must not land on the Ionic web destination.
  return AppEntrypoint.fh_plus_mobile;
};
